import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, forwardRef, inject, OnDestroy, OnInit, Output, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DTypeForm, DTypeFormField } from '@grupo-constant/next-dtype/form';
import {
    AUTOCOMPLETEFROMENUMERATION_COLUMN_URI,
    AUTOCOMPLETEFROMURI_COLUMN_URI,
    AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA, CODEBARSCANNERWITHSELECT_URI, CODEBAR_SCANNER_URI, Command,
    CommandAction,
    Condition,
    ConditionPath,
    DType,
    DTypeCol,
    DTypeStatus,
    DTypeViewableCol,
    EMBEDDED_COLLECTION_COLUMN_URI,
    EMBEDDED_ITEM_COLUMN_URI,
    FilterValues,
    FilterValuesCondition,
    NextLocator,
    SELECTFACADE_COLUMN_URI,
    SELECTFROMCONCEPTSERVICE_COLUMN_URI,
    SELECTFROMENUMERATION_COLUMN_URI,
    SELECTFROMMASTERDATA_COLUMN_URI,
    SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI,
    SelectFromUri,
    SELECTFROMURI_COLUMN_URI,
    SELECTMASTER_COLUMN_URI,
    SELECTNEXTTASKTYPE_COLUMN_URI,
    SelectOption, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI, SELECTSERVICE_COLUMN_URI, SelectWithGivenOptions,
    SELECTWITHGIVENOPTIONS_COLUMN_URI, Table, TEXT_COLUMN_URI, TRIGGER_COLUMN_URI
} from '@grupo-constant/next-dtype/types';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { isArray } from 'lodash-es';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange } from '@angular/material/select';
import { DtypeItemComponent } from '../dtype-item/dtype-item.component';
import { DTypeService } from '../dtype.service';
import { DtypeConditionDialogComponent } from './dtype-condition/dtype-condition.dialog';
import { LocalizePipe } from '../../pipes/standalone/localize.pipe';
import { FuseScrollbarDirective } from '../../../../@fuse/directives/scrollbar/scrollbar.directive';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation/confirmation.service';
import { HttpService } from '../../http/http.service';
import { LocalizeService } from '../../pipes/localize.service';
import { Net2BeepleMethodsEnum } from '../../enums/net2beeple-methods.enum';

@Component({
    selector: 'dtype-field-edit',
    templateUrl: './dtype-field-edit.dialog.html',
    styles: [
        /* language=SCSS */
        `
            .dtype-field-edit-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    imports: [CommonModule, LocalizePipe, TranslocoModule, MatIconModule, MatProgressBarModule, MatCheckboxModule,
        MatFormFieldModule, MatSlideToggleModule, FormsModule, forwardRef(() => DtypeItemComponent), MatButtonModule, MatDialogModule,
        FuseScrollbarDirective],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class DtypeFieldEditDialogComponent implements OnInit, OnDestroy {

    @Output() update = new EventEmitter<DTypeCol>();
    @Output() updateDtype = new EventEmitter<DType>();

    dtypeUri: string;
    dtypeForm: DTypeForm;
    dtype: DType;
    dtypeColumn: DTypeCol;
    locator: NextLocator;
    readonly: boolean;

    oldName: string;
    isUpdate = false;
    isLoading = false;

    hasChanges = false;

    embeddedItemUri = EMBEDDED_ITEM_COLUMN_URI;
    embeddedItemCollectionUri = EMBEDDED_COLLECTION_COLUMN_URI;

    fieldsWithCondition = ['readonly', 'hidden', 'mandatory'];
    currentCommand: Command;
    currentConditionField: string;
    availableConditions: ConditionPath[];
    showConditions = false;
    operatorsOptions: SelectOption[];
    selection = new SelectionModel<string>(true, []);

    // Filters
    masterFields: DTypeCol[];
    dtypeFields: DTypeCol[];
    parentDtypeFields: DTypeCol[];

    // Featured oids
    filterControl = new FormControl<string>('');
    options: SelectOption[] = [];
    filteredOptions: Observable<SelectOption[]>;

    private unsubscribeAll = new Subject<void>();

    private readonly operatorEnumClass = 'com.cloudcodice.feature.dtype.condition.Operator';

    public data: {
        dtypeColumn?: DTypeCol;
        dtypeUri: string;
        locator: NextLocator;
        readonly?: boolean;
        dtype?: DType;
        parentDtype?: DType;
        table?: Table;
        isUpdate: boolean;
    } = inject(MAT_DIALOG_DATA);
    private dialogsService = inject(DialogsService);
    private dtypeService = inject(DTypeService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private httpService = inject(HttpService);
    private localize = inject(LocalizeService);
    private _matDialog = inject(MatDialog);
    public matDialogRef = inject(MatDialogRef<DtypeFieldEditDialogComponent, DTypeCol>);
    private translocoService = inject(TranslocoService);

    async ngOnInit() {
        if (this.data.dtypeUri) {
            this.isLoading = true;
            this.dtypeUri = this.data.dtypeUri;
            this.dtypeColumn = { ...this.data.dtypeColumn };
            this.dtypeColumn.command = this.dtypeColumn.command ?? [];
            this.isUpdate = this.data.isUpdate;
            this.locator = this.data.locator;
            this.readonly = this.data.readonly ?? false;
            if (this.isUpdate) {
                this.oldName = structuredClone(this.dtypeColumn.name);
            }
            this.dtypeForm = await this.dtypeService.getDtypeFormByUri({
                uri: this.dtypeUri,
                locator: this.locator,
                status: DTypeStatus.DESIGN,
                bindableOptions: true
            }, this.dtypeColumn, {
                readonly: this.readonly,
                isUpdate: this.isUpdate
            });
            this.dtype = structuredClone(this.data.dtype);
            this.availableConditions = this.dtype.path.filter(p => p.field !== this.dtypeColumn.name) ?? [];
            if (this.acceptFilters()) {
                this.dtypeFields = (this.dtype.column ?? []).filter(col => this.fieldAvailableForFilters(col));
                this.parentDtypeFields = (this.data.parentDtype?.column ?? []).filter(col => this.fieldAvailableForFilters(col));
                if (this.dtypeForm.fields['uri'] && this.dtypeForm.fields['uri'].control.value) {
                    await this.fillMasterFields(this.dtypeForm.fields['uri'].control.value);
                }
                for (const filter of (this.dtypeColumn as SelectFromUri).filter) {
                    if (filter.value) {
                        filter.type = 'value';
                    } else {
                        filter.type = 'otherfield';
                    }
                }
                this.dtypeForm.fields['uri'].control.valueChanges
                    .pipe(takeUntil(this.unsubscribeAll))
                    .subscribe(async (uri) => {
                        if (uri) {
                            await this.fillMasterFields(uri);
                        }
                    });
            }
            this.isLoading = false;
        } else {
            this.dialogsService.openErrorDialog();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    /**
     * Save the column with the form values and close the dialog
     */
    async save() {
        await this.saveChanges();
    }

    openConditions(field: string) {
        if (!this.operatorsOptions || this.operatorsOptions.length === 0) {
            this.httpService.post<{ option: SelectOption[] }>(Net2BeepleMethodsEnum.retrieveEnumerationType, { enumerationClass: this.operatorEnumClass })
                .subscribe(reply => this.operatorsOptions = reply.option);
        }
        const value = this.dtypeForm.control.get(field)?.value;
        if (typeof value === 'boolean') {
            this.currentConditionField = value ? field : 'no' + field;
            let action: CommandAction;
            switch (field) {
                case 'hidden':
                    action = value ? CommandAction.HIDDEN : CommandAction.NOT_HIDDEN;
                    break;
                case 'mandatory':
                    action = value ? CommandAction.MANDATORY : CommandAction.NOT_MANDATORY;
                    break;
                case 'readonly':
                    action = value ? CommandAction.READONLY : CommandAction.NOT_READONLY;
                    break;
            }
            const command = this.dtypeColumn.command.find(com => com.action === action);
            this.currentCommand = command;
            if (!this.currentCommand) {
                this.currentCommand = {
                    action,
                    clear: true,
                    condition: [],
                    matchAll: false,
                    path: [],
                };
                this.dtypeColumn.command.push(this.currentCommand);
            }
            this.selection.clear();
            for (const path of this.availableConditions) {
                if (this.currentCommand.path?.includes(path.path)) {
                    this.selection.select(path.path);
                }
            }
        }
        this.showConditions = true;
    }

    closeConditions() {
        this.showConditions = false;
    }

    async saveConditions() {
        const confirmDialog = this.openConfirmDialog(this.translocoService.translate('saveCommandConfirm'));
        const confirm = await firstValueFrom(confirmDialog.afterClosed());
        if (confirm === 'confirmed') {
            const selectedConditions = this.availableConditions.filter(cond => this.selection.selected.includes(cond.path));
            this.currentCommand.path = this.dtype.path
                .filter(path => selectedConditions.findIndex(cond => cond.path === path.path) > -1)
                .map(path => path.path);
            this.showConditions = false;
            this.saveChanges(false, true);
        }
    }

    addCondition() {
        const dialogRef = this._matDialog.open(DtypeConditionDialogComponent, {
            autoFocus: false,
            disableClose: true,
            panelClass: 'dtype-condition-dialog-panel',
            height: '50vh',
            width: '50vw',
            maxHeight: '500px',
            data: {
                field: this.dtypeColumn,
                fields: this.dtype.column,
                operatorsOptions: this.operatorsOptions,
                conditions: this.dtype.path,
                dtype: this.dtype,
                locator: this.locator
            },
        });

        dialogRef.afterClosed().subscribe((condition: Condition) => {
            if (condition) {
                if (!this.dtype.path) {
                    this.dtype.path = [];
                }
                let newPath = 0;
                if (this.dtype.path.length > 0) {
                    newPath = Number(this.dtype.path[this.dtype.path.length - 1].path.substring(4)) + 1;
                }
                const conditionPath: ConditionPath = {
                    field: condition.field,
                    fieldType: condition.fieldType,
                    operand: condition.operand,
                    operator: condition.operator,
                    path: 'PATH' + String(newPath).padStart(3, '0'),
                    isReadOnly: false
                };
                this.dtype.path.push(conditionPath);
                this.availableConditions.push(conditionPath);
                this.saveChanges(false, true, true);
            }
        });
    }

    editCondition(conditionPath: ConditionPath) {
        if (conditionPath.isReadOnly) {
            return;
        }
        const dialogRef = this._matDialog.open(DtypeConditionDialogComponent, {
            autoFocus: false,
            disableClose: true,
            panelClass: 'dtype-condition-dialog-panel',
            height: '50vh',
            width: '50vw',
            maxHeight: '500px',
            data: {
                field: this.dtypeColumn,
                fields: this.data.dtype.column,
                operatorsOptions: this.operatorsOptions,
                condition: structuredClone(conditionPath),
                conditions: this.data.dtype.path,
                dtype: this.dtype,
                locator: this.locator
            },
        });

        dialogRef.afterClosed().subscribe((condition: Condition) => {
            if (condition) {
                if (!this.dtype.path) {
                    this.dtype.path = [];
                }
                const path = this.dtype.path.find(p => p.field === conditionPath.field && p.operator === conditionPath.operator
                    && p.operand === conditionPath.operand);
                if (path) {
                    path.field = condition.field;
                    path.fieldType = condition.fieldType;
                    path.operand = condition.operand;
                    path.operator = condition.operator;
                }
                conditionPath.field = condition.field;
                conditionPath.fieldType = condition.fieldType;
                conditionPath.operand = condition.operand;
                conditionPath.operator = condition.operator;
                this.saveChanges(false, true, true);
            }
        });
    }

    async removeCondition(event: MouseEvent, index: number) {
        event.stopPropagation();
        const dialog = this.openConfirmDialog(this.translocoService.translate('removeConditionsConfirm'));
        dialog.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                const condition = this.availableConditions[index];
                if (condition) {
                    const trueIndex = this.dtype.path.findIndex(p => p.field === condition.field && p.operator === condition.operator
                        && p.operand === condition.operand);
                    if (trueIndex > -1) {
                        if (this.selection.isSelected(condition.path)) {
                            this.selection.deselect(condition.path);
                        }
                        this.dtype.path.splice(trueIndex, 1);
                        for (const command of this.dtypeColumn.command ?? []) {
                            command.path = command.path.filter(path => condition.path !== path);
                        }
                        for (const col of this.dtype.column) {
                            for (const command of col.command ?? []) {
                                command.path = command.path.filter(path => condition.path !== path);
                            }
                        }
                    }
                }
                this.availableConditions.splice(index, 1);
                this.saveChanges(false, true, true);
            }
        });
    }

    toggleMatchAll(change: MatSlideToggleChange, type: 'no' | 'yes') {
        this.currentCommand.matchAll = type === 'no' ? !change.checked : change.checked;
    }

    displayOperand(operand: any) {
        if (isArray(operand)) {
            let str = '';
                for (const op of operand) {
                    str += str.length === 0 ? this.localize.transform(op) : `, ${this.localize.transform(op)}`;
                }
                return str;
        } else if (typeof operand === 'object') {
            return this.localize.transform(operand.value);
        } else {
            return operand;
        }
    }

    conditionFieldIsSelectOrAutocomplete(fieldType: string) {
        return [
            SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI,
            SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI,
            SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, CODEBARSCANNERWITHSELECT_URI,
            SELECTSERVICE_COLUMN_URI, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI,
            AUTOCOMPLETEFROMENUMERATION_COLUMN_URI, AUTOCOMPLETEFROMURI_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA,
            AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI
        ].includes(fieldType);
    }

    acceptFilters() {
        return this.dtypeUri === SELECTFROMURI_COLUMN_URI || this.dtypeUri === AUTOCOMPLETEFROMURI_COLUMN_URI || this.dtypeUri === CODEBARSCANNERWITHSELECT_URI;
    }

    acceptFeatured() {
        return this.dtypeUri === SELECTFROMURI_COLUMN_URI;
    }

    addFilter() {
        if (!(this.dtypeColumn as SelectFromUri).filter) {
            (this.dtypeColumn as SelectFromUri).filter = [];
        }
        const filter = (this.dtypeColumn as SelectFromUri).filter;
        filter.push({
            condition: FilterValuesCondition.EQUALS,
            innerField: '',
            outerField: '',
            regexpr: [],
            type: 'otherfield'
        });
    }

    removeFilter(index: number) {
        if (!(this.dtypeColumn as SelectFromUri).filter) {
            (this.dtypeColumn as SelectFromUri).filter = [];
        }
        const filter = (this.dtypeColumn as SelectFromUri).filter;
        if (filter.length > index) {
            filter.splice(index, 1);
        }
    }

    filterTypeChanged(event: MatSelectChange, filter: FilterValues) {
        if (event.value === 'otherfield') {
            filter.value = undefined;
        } else if (event.value === 'value') {
            filter.outerField = undefined;
        }
    }


    getConditionSelectFieldCaption(condition: ConditionPath) {
        const field = this.dtype.column.find(c => c.name === condition.field);
        if (field) {
            let selectOptions: SelectOption[];
            if ((field as SelectWithGivenOptions).option) {
                selectOptions = (field as SelectWithGivenOptions).option;
            } else {
                selectOptions = (field as SelectFromUri).uri ? this.data.dtype.link?.[(field as SelectFromUri).uri] : []
            }
            if (isArray(condition.operand)) {
                let str = '';
                for (const op of condition.operand) {
                    const option = selectOptions.find(o => o.code === op);
                    if (option) {
                        str += str.length === 0 ? this.localize.transform(option.value) : `, ${this.localize.transform(option.value)}`;
                    }
                }
                return str;
            } else {
                const option = selectOptions.find(o => o.code === condition.operand);
                if (option) {
                    return this.localize.transform(option.value);
                }
            }
        }
        return '';
    }

    /**
     * Marca o desmarca todos los checks de la tabla
     */
    masterToggle(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.selection.clear();
            for (const cond of this.dtype?.path ?? []) {
                this.selection.toggle(cond.path);
            };
        }
    }

    isAllSelected(): boolean {
        return this.selection.selected.length === this.dtype?.path?.length;
    }

    fieldHasPath(field: DTypeFormField) {
        const value = field.control.value;
        let action: CommandAction;
            switch (field.name) {
                case 'hidden':
                    action = value ? CommandAction.HIDDEN : CommandAction.NOT_HIDDEN;
                    break;
                case 'mandatory':
                    action = value ? CommandAction.MANDATORY : CommandAction.NOT_MANDATORY;
                    break;
                case 'readonly':
                    action = value ? CommandAction.READONLY : CommandAction.NOT_READONLY;
                    break;
            }
        return this.dtypeColumn.command?.findIndex(c => c.action === action && c.path?.length > 0) > -1;
    }

    /**
     * Save the column with the form values
     */
    /* apply() {
        this.saveChanges(false);
    } */

    private async saveChanges(closeDialog = true, confirmed = false, conditionsOpened = false) {
        let confirm: 'confirmed' | 'cancelled';
        if (!confirmed) {
            const confirmDialog = this.openConfirmDialog(this.translocoService.translate('saveColumnConfirm'));
            confirm = await firstValueFrom(confirmDialog.afterClosed());
        }
        if (confirmed || confirm === 'confirmed') {
            this.dtypeForm.control.markAllAsTouched();
            if (this.dtypeForm.control.valid) {
                this.dtypeColumn = Object.assign(this.dtypeColumn, this.dtypeForm.control.getRawValue());
                if (this.acceptFilters()) {
                    const column = (this.dtypeColumn as SelectFromUri);
                    column.filter = (column.filter ?? []).filter(f => this.isFilterValid(f));
                }
                const columns = this.dtype.column.map(c => c.name.toLowerCase());
                if (this.oldName !== this.dtypeColumn.name && columns.includes(this.dtypeColumn.name.toLowerCase())) {
                    this.dialogsService.openErrorDialog({
                        message: this.translocoService.translate('errorFieldNameNotAvailable')
                    });
                    return;
                }
                this.dtypeColumn.type = this.data.dtypeUri;
                if (!conditionsOpened) {//* Se borran los command sin path
                    if (this.dtypeColumn.command?.length > 0) {
                        this.dtypeColumn.command = this.dtypeColumn.command.filter(command => command.path && command.path.length > 0);
                    }
                    for (const col of this.dtype.column) {
                        col.command = (col.command ?? []).filter(command => command.path && command.path.length > 0);
                    }
                }
                this.deleteInvalidCommands();
                this.data.dtype = this.dtype;
                if (closeDialog) {
                    this.matDialogRef.close(this.dtypeColumn);
                } else {
                    this.update.emit(this.dtypeColumn);
                }
                this.updateDtype.emit(this.dtype);
            } else {
                this.dialogsService.openErrorDialog({
                    message: this.translocoService.translate('errorsInForm')
                });
            }
        }
    }

    private deleteInvalidCommands() {
        if (this.dtypeColumn.command && this.dtypeColumn.command.length > 0) {
            const viewableCol = this.dtypeColumn as DTypeViewableCol;
            const commandsToDelete: number[] = [];
            this.dtypeColumn.command.forEach((command, index) => {
                switch (command.action) {
                    case CommandAction.HIDDEN:
                        if (!viewableCol.hidden) {
                            commandsToDelete.push(index);
                        }
                        break;
                    case CommandAction.NOT_HIDDEN:
                        if (viewableCol.hidden) {
                            commandsToDelete.push(index);
                        }
                        break;
                    case CommandAction.MANDATORY:
                        if (!viewableCol['mandatory']) {
                            commandsToDelete.push(index);
                        }
                        break;
                    case CommandAction.NOT_MANDATORY:
                        if (viewableCol['mandatory']) {
                            commandsToDelete.push(index);
                        }
                        break;
                    case CommandAction.READONLY:
                        if (!viewableCol.readonly) {
                            commandsToDelete.push(index);
                        }
                        break;
                    case CommandAction.NOT_READONLY:
                        if (viewableCol.readonly) {
                            commandsToDelete.push(index);
                        }
                        break;
                }
            });
            for (const index of commandsToDelete) {
                this.dtypeColumn.command.splice(index, 1);
            }
        }
    }

    private async fillMasterFields(uri: string) {
        const locator = this.dtypeService.getLocatorFromString(uri.substring(9, uri.lastIndexOf('/'))); //master://
        const masterDType = await this.dtypeService.getDatatype({
            retrieveDTypeBody: {
                uri,
                locator,
                bindableOptions: true
            }
        });
        this.masterFields = (masterDType?.column ?? []).filter(col => this.fieldAvailableForFilters(col));
    }

    private fieldAvailableForFilters(column: DTypeCol) {
        const availableTypes = [TEXT_COLUMN_URI, SELECTFROMURI_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETEFROMURI_COLUMN_URI,
            AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, CODEBARSCANNERWITHSELECT_URI, CODEBAR_SCANNER_URI, TRIGGER_COLUMN_URI];
        return availableTypes.includes(column.type);
    }

    private isFilterValid(filter: FilterValues) {
        return ((filter.outerField && filter.type === 'otherfield') || (filter.value && filter.type === 'value'))
            && filter.innerField && filter.condition;
    }

    private openConfirmDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.translocoService.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('cancel')
                }
            }
        });
    }
}
